// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import diceSkinApi from './diceSkin.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchDiceSkinApi = createAsyncThunk(
    'itemSaga/diceSkin/fetchDiceSkinApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await diceSkinApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleDiceSkinPublishedStateApi = createAsyncThunk(
    'itemSaga/diceSkin/toggleDiceSkinPublishedStateApi',
    async (row, { dispatch, rejectWithValue }) => {
        try {
            await diceSkinApi.update(row.id, {is_ready: !row.is_ready});
            return row.id;
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue([]);
        }
    },
);

export const createDiceSkinApi = createAsyncThunk(
    'itemSaga/diceSkin/createDiceSkinApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await diceSkinApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeDiceSkinApi = createAsyncThunk(
    'itemSaga/diceSkin/storeDiceSkinApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const diceSkin = await diceSkinApi.store(values);
            alertSwal('success', 'DiceSkin created successfully!');

            return diceSkin;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showDiceSkinApi = createAsyncThunk(
    'itemSaga/diceSkin/showDiceSkinApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const data = await diceSkinApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateDiceSkinApi = createAsyncThunk(
    'itemSaga/diceSkin/updateDiceSkinApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { navigate, id, values } = params;
        const state = getState();

        try {
            dispatch(requestStarted());
            await diceSkinApi.update(id, values);
            alertSwal('success', 'DiceSkin updated successfully!');

            const { ranks } = state.itemSaga.diceSkinMetaData;
            const updatedRank = ranks.find((item) => item.id === values.rankId);

            return { values, updatedRank };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteDiceSkinApi = createAsyncThunk('itemSaga/diceSkin/deleteDiceSkinApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await diceSkinApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const diceSkinSlice = createSlice({
    name: 'itemSaga/diceSkin',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedDiceSkin(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDiceSkinApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchDiceSkinApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleDiceSkinPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.is_ready = !updateRow.is_ready;
            })
            .addCase(toggleDiceSkinPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createDiceSkinApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(createDiceSkinApi.rejected, () => {
                //
            });

        builder
            .addCase(storeDiceSkinApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeDiceSkinApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showDiceSkinApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload;
            })
            .addCase(showDiceSkinApi.rejected, () => {
                //
            });

        builder
            .addCase(updateDiceSkinApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.values.id);
                updatedRow.name = payload.values.name;
                updatedRow.statPoint = payload.values.statPoint;
                updatedRow.rank = payload.updatedRank.name;
            })
            .addCase(updateDiceSkinApi.rejected, () => {
                //
            });

        builder.addCase(deleteDiceSkinApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedDiceSkin } = diceSkinSlice.actions;
